import { Clock, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { usePendientesCampanias } from '@/modules/multichannel/hooks/useCampanias'

interface PendientesEnvioProps {
  filters?: { fecha_desde?: string; fecha_hasta?: string }
}

export function PendientesEnvio({ filters }: PendientesEnvioProps) {
  const navigate = useNavigate()
  const { data: pendientes, isLoading } = usePendientesCampanias(filters)

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Sin fecha'
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return new Intl.DateTimeFormat('es-EC', {
      month: 'short',
      day: 'numeric',
    }).format(date)
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={{ background: 'linear-gradient(to right, #f6c23e10, transparent 60%)' }}
    >
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full p-1.5" style={{ backgroundColor: '#f6c23e15' }}>
              <Clock className="h-4 w-4" style={{ color: '#f6c23e' }} />
            </div>
            <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Pendientes de Envío</span>
          </div>
          {pendientes && pendientes.length > 0 && (
            <Badge className="badge-gradient-yellow hover:opacity-90 text-xs">
              {pendientes.length}
            </Badge>
          )}
        </div>
      </div>
      <div className="px-4 pb-4">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !pendientes || pendientes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay campañas pendientes
          </p>
        ) : (
          <div className="max-h-[200px] overflow-y-auto">
            <div className="relative border-l-2 border-gray-200 dark:border-gray-700 ml-2 space-y-3">
              {pendientes.map((campania) => (
                <div key={campania.id} className="relative pl-5">
                  <div className="absolute -left-[5px] top-2 h-2 w-2 rounded-full" style={{ backgroundColor: '#f6c23e' }} />
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{campania.nombre_campania}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{campania.marca_principal_nombre || campania.marca}</span>
                        <span>&middot;</span>
                        <span>{formatDate(campania.fecha_publicacion)}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 shrink-0"
                      onClick={() => navigate(`/multichannel/campania/${campania.id}`)}
                      title="Ver campaña"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
